import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useLang } from "@/i18n/LangContext";
import { Plus, Trash2, Copy, Ban, CheckCircle2, X, Search } from "lucide-react";

export default function AdminKeys() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [genOpen, setGenOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const { data: keys = [] } = useQuery({
    queryKey: ["admin-keys"],
    queryFn: async () => (await api.get("/keys")).data,
  });
  const { data: products = [] } = useQuery({
    queryKey: ["admin-products-min"],
    queryFn: async () => (await api.get("/products/admin/list")).data,
  });
  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const filtered = keys.filter((k) => !filter || k.code.includes(filter.toUpperCase()));

  const del = async (k) => {
    if (!window.confirm("Delete this key?")) return;
    await api.delete(`/keys/${k.id}`);
    qc.invalidateQueries({ queryKey: ["admin-keys"] });
    toast.success("Deleted");
  };

  const toggleStatus = async (k) => {
    const newStatus = k.status === "active" ? "disabled" : "active";
    await api.put(`/keys/${k.id}`, { status: newStatus });
    qc.invalidateQueries({ queryKey: ["admin-keys"] });
    toast.success("Updated");
  };

  return (
    <div className="p-8" data-testid="admin-keys-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Access Codes</div>
          <h1 className="font-heading text-3xl font-bold">{t("admin.keys")}</h1>
        </div>
        <button onClick={() => setGenOpen(true)} className="bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 glow-blue" data-testid="generate-keys-btn">
          <Plus className="w-4 h-4" /> Generate Keys
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search code..." className="bg-[#080808] border border-white/10 rounded ps-9 pe-3 py-2 w-72" data-testid="keys-search" />
      </div>

      <div className="bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-start px-4 py-3">Code</th>
              <th className="text-start px-4 py-3">Products</th>
              <th className="text-start px-4 py-3">Uses</th>
              <th className="text-start px-4 py-3">Status</th>
              <th className="text-start px-4 py-3">Expires</th>
              <th className="text-start px-4 py-3">Created</th>
              <th className="text-start px-4 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((k) => (
              <tr key={k.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-mono text-xs">
                  {k.code}{" "}
                  <button onClick={async () => { const ok = await copyToClipboard(k.code); if (ok) toast.success("Copied"); else toast.error("Copy failed"); }} className="text-zinc-500 hover:text-white" data-testid={`copy-key-${k.id}`}><Copy className="w-3 h-3 inline" /></button>
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs">
                  {!k.product_ids?.length ? <span className="text-brand-cyan">ANY</span> :
                    k.product_ids.map((id) => productMap[id]?.title).filter(Boolean).join(", ")}
                </td>
                <td className="px-4 py-3 font-mono text-xs tabular-nums">{k.uses_remaining ?? 0}/{k.max_uses ?? 1}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                    k.status === "active" ? "bg-brand-green/10 text-brand-green border border-brand-green/30" :
                    k.status === "used" ? "bg-blue-500/10 text-blue-400 border border-blue-500/30" :
                    "bg-white/5 text-zinc-500 border border-white/10"
                  }`}>{k.status}</span>
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{k.expires_at ? new Date(k.expires_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(k.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {k.status !== "used" && (
                      <button onClick={() => toggleStatus(k)} className="p-2 hover:bg-white/5 rounded">
                        {k.status === "active" ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-brand-green" />}
                      </button>
                    )}
                    <button onClick={() => del(k)} className="p-2 hover:bg-red-500/10 text-red-400 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-zinc-500">{t("common.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {genOpen && <GenerateModal products={products} onClose={() => setGenOpen(false)} onDone={() => { setGenOpen(false); qc.invalidateQueries({ queryKey: ["admin-keys"] }); }} />}
    </div>
  );
}

function GenerateModal({ products, onClose, onDone }) {
  const [form, setForm] = useState({ count: 10, product_ids: [], expires_in_days: "", prefix: "KHA", max_uses: 1 });
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/keys/generate", {
        count: parseInt(form.count) || 1,
        product_ids: form.product_ids,
        prefix: form.prefix,
        expires_in_days: form.expires_in_days ? parseInt(form.expires_in_days) : null,
        max_uses: parseInt(form.max_uses) || 1,
      });
      setCreated(r.data);
      toast.success(`${r.data.count} keys generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (id) => {
    setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0c0c0c] border border-white/10 rounded-xl max-w-xl w-full" data-testid="generate-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <h3 className="font-heading text-lg font-bold">Generate Keys</h3>
          <button onClick={() => { setCreated(null); onClose(); }}><X className="w-5 h-5" /></button>
        </div>
        {created ? (
          <div className="p-6">
            <div className="text-sm text-brand-green mb-3">✓ Generated {created.count} keys (batch {created.batch_id})</div>
            <textarea readOnly value={created.keys.map((k) => k.code).join("\n")} className="w-full h-64 bg-[#080808] border border-white/10 rounded p-3 font-mono text-xs" data-testid="generated-keys-list" />
            <div className="mt-3 flex gap-2">
              <button onClick={async () => { const ok = await copyToClipboard(created.keys.map((k) => k.code).join("\n")); if (ok) toast.success("Copied"); else toast.error("Copy failed"); }} className="px-4 py-2 rounded bg-white/5 hover:bg-white/10" data-testid="copy-all-keys-btn">Copy All</button>
              <button onClick={onDone} className="px-4 py-2 rounded bg-brand">Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Input label="Count *" type="number" value={form.count} onChange={(v) => setForm({ ...form, count: v })} testid="gen-count" />
              <Input label="Prefix" value={form.prefix} onChange={(v) => setForm({ ...form, prefix: v })} testid="gen-prefix" />
              <Input label="Expires (days, optional)" type="number" value={form.expires_in_days} onChange={(v) => setForm({ ...form, expires_in_days: v })} testid="gen-expires" />
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">Number of Games</div>
                <select
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 1 })}
                  className="w-full bg-[#080808] border border-white/10 rounded px-3 py-2 focus:border-brand outline-none"
                  data-testid="gen-max-uses"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>{n} {n === 1 ? "game" : "games"}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">Allowed products (none = any)</div>
                <div className="bg-[#080808] border border-white/10 rounded p-3 max-h-40 overflow-y-auto space-y-1">
                  {products.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                      {p.title} <span className="text-zinc-500 text-xs">({p.platform})</span>
                    </label>
                  ))}
                  {products.length === 0 && <div className="text-zinc-500 text-xs">Add products first to restrict keys.</div>}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10">Cancel</button>
              <button disabled={loading} onClick={submit} className="px-5 py-2 rounded-md bg-brand hover:bg-brand-hover font-semibold disabled:opacity-50" data-testid="gen-submit">
                {loading ? "Generating..." : "Generate"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">{label}</div>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#080808] border border-white/10 rounded px-3 py-2 focus:border-brand outline-none" data-testid={testid} />
    </div>
  );
}
